import { useState } from 'react';
import {
  UserPlus,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Copy,
  Check,
  Mail,
  Users,
} from 'lucide-react';
import {
  usePlatformOperators,
  usePlatformUsers,
  useCreateOperatorAdmin,
} from '@/hooks/api/usePlatform';
import { PageHeader } from '@/components/ui/PageHeader';
import { extractErrorMessage } from '@/lib/error';
import { userDisplayName } from '@/lib/user-display';
import type {
  CreateOperatorAdminResult,
  CreateOperatorAdminRequest,
  PlatformUser,
} from '@/services/api/platform.api';

// ── Role presentation (keyed by the raw role string from the API) ──────────────
const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  revenue_manager: 'Revenue Manager',
  operations: 'Operations',
  owner: 'Owner',
};
const ROLE_STYLES: Record<string, string> = {
  super_admin: 'bg-gray-900 text-white',
  admin: 'bg-purple-100 text-purple-700',
  revenue_manager: 'bg-blue-100 text-blue-700',
  operations: 'bg-green-100 text-green-700',
  owner: 'bg-amber-100 text-amber-700',
};
function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role.replace(/_/g, ' ');
}
function roleStyle(role: string): string {
  return ROLE_STYLES[role] ?? 'bg-gray-100 text-gray-700';
}

/** Compact relative hint, e.g. "2h ago", from a past millisecond delta. */
function relativeTime(diffMs: number): string {
  const sec = Math.max(0, Math.round(diffMs / 1000));
  if (sec < 60) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.round(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
}

/** Absolute + relative rendering of a last-login ISO string (null → "Never"). */
function formatLastLogin(iso: string | null): { abs: string; rel: string } | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const abs = d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return { abs, rel: relativeTime(Date.now() - d.getTime()) };
}

function fullName(u: PlatformUser): string {
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return name || '—';
}

function StatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${
        isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${isActive ? 'bg-green-500' : 'bg-gray-400'}`}
        aria-hidden="true"
      />
      {isActive ? 'Active' : 'Disabled'}
    </span>
  );
}

function LastLoginCell({ iso }: { iso: string | null }) {
  const login = formatLastLogin(iso);
  if (!login) {
    return <span className="text-gray-400">Never</span>;
  }
  return (
    <span className="text-gray-700" title={login.abs}>
      {login.abs}
      <span className="ml-1.5 text-xs text-gray-400">({login.rel})</span>
    </span>
  );
}

function TeamMembersTable() {
  const { data, isLoading, isError, refetch, isFetching } = usePlatformUsers();

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
        <div
          className="flex items-center justify-center w-9 h-9 rounded-lg bg-brand-100 shrink-0"
          aria-hidden="true"
        >
          <Users size={16} className="text-brand-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-semibold text-gray-900">Team members</h2>
          <p className="text-sm text-gray-500">
            Every BestAcco team login and their most recent sign-in.
          </p>
        </div>
        {isFetching && !isLoading && (
          <Loader2
            size={16}
            className="animate-spin text-gray-400"
            aria-label="Refreshing"
          />
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 px-6 py-12 text-sm text-gray-500">
          <Loader2 size={16} className="animate-spin" aria-hidden="true" />
          Loading team members…
        </div>
      ) : isError ? (
        <div
          className="flex items-center gap-2 px-6 py-8 text-sm text-red-700"
          role="alert"
        >
          <AlertCircle size={16} aria-hidden="true" />
          <span>Unable to load team members.</span>
          <button
            type="button"
            onClick={() => void refetch()}
            className="font-medium text-brand-600 hover:text-brand-700 underline"
          >
            Retry
          </button>
        </div>
      ) : !data || data.length === 0 ? (
        <div className="px-6 py-12 text-center text-sm text-gray-500">
          No team members yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                <th scope="col" className="px-6 py-3">Name</th>
                <th scope="col" className="px-6 py-3">Email</th>
                <th scope="col" className="px-6 py-3">Operator</th>
                <th scope="col" className="px-6 py-3">Role</th>
                <th scope="col" className="px-6 py-3">Status</th>
                <th scope="col" className="px-6 py-3">Last login</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-6 py-3 font-medium text-gray-900 whitespace-nowrap">
                    {fullName(u)}
                  </td>
                  <td className="px-6 py-3 text-gray-600">{u.email ?? '—'}</td>
                  <td className="px-6 py-3 text-gray-600 whitespace-nowrap">
                    {u.operatorName}
                  </td>
                  <td className="px-6 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${roleStyle(u.role)}`}
                    >
                      {roleLabel(u.role)}
                    </span>
                  </td>
                  <td className="px-6 py-3">
                    <StatusBadge isActive={u.isActive} />
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <LastLoginCell iso={u.lastLoginAt} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Split a free-text "Full name" into firstName/lastName for the backend DTO
 * (which takes firstName?/lastName?, not a single `name`). First token is the
 * first name; everything after is the last name. Empty → undefined.
 */
function splitName(fullName: string): {
  firstName?: string;
  lastName?: string;
} {
  const trimmed = fullName.trim();
  if (!trimmed) return {};
  const parts = trimmed.split(/\s+/);
  const firstName = parts.shift();
  const lastName = parts.length ? parts.join(' ') : undefined;
  return {
    ...(firstName ? { firstName } : {}),
    ...(lastName ? { lastName } : {}),
  };
}

function TempPasswordConfirmation({
  result,
  groupCode,
  onDismiss,
}: {
  result: CreateOperatorAdminResult;
  /** The operator's group code (tenant slug) — shown for username-only logins. */
  groupCode: string | undefined;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);
  // A username-only login has no email: no invite was sent, so the admin must
  // relay the group code + username + this password directly.
  const isUsernameOnly = !result.user.email && !!result.user.username;
  const label = userDisplayName(result.user);

  async function copyPassword() {
    try {
      await navigator.clipboard.writeText(result.temporaryPassword);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked — the value stays visible for manual copy.
    }
  }

  return (
    <div
      className="rounded-xl border border-green-200 bg-green-50 p-5"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <CheckCircle2 size={20} className="text-green-600 shrink-0 mt-0.5" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-green-900">
            Login created for {label}
          </p>
          <p className="mt-1 text-sm text-green-800">
            Share this one-time password with them; they&apos;ll be prompted to
            change it on first login.
          </p>

          <div className="mt-3 flex items-center gap-2">
            <code className="flex-1 rounded-lg border border-green-300 bg-white px-3 py-2 font-mono text-sm text-gray-900 break-all">
              {result.temporaryPassword}
            </code>
            <button
              type="button"
              onClick={() => void copyPassword()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-green-300 bg-white px-3 py-2 text-sm font-medium text-green-800 hover:bg-green-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400 shrink-0"
              aria-label="Copy temporary password"
            >
              {copied ? (
                <>
                  <Check size={15} aria-hidden="true" /> Copied
                </>
              ) : (
                <>
                  <Copy size={15} aria-hidden="true" /> Copy
                </>
              )}
            </button>
          </div>

          {isUsernameOnly ? (
            <p className="mt-3 text-xs text-green-700">
              No email sent — share these credentials directly. Give them: group
              code{' '}
              <span className="font-mono font-semibold break-all">
                {groupCode ?? '—'}
              </span>{' '}
              &middot; username{' '}
              <span className="font-mono font-semibold break-all">
                {result.user.username}
              </span>{' '}
              &middot; this password.
            </p>
          ) : (
            <p className="mt-3 flex items-center gap-1.5 text-xs text-green-700">
              <Mail size={14} aria-hidden="true" />
              An invite email with sign-in details was sent to {result.user.email}.
            </p>
          )}
          <p className="mt-1 text-xs text-green-700">
            This password is shown once and can&apos;t be retrieved later.
          </p>

          <button
            type="button"
            onClick={onDismiss}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
          >
            Add another user
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminUsersPage() {
  const {
    data: operators,
    isLoading: operatorsLoading,
    isError: operatorsError,
    refetch: refetchOperators,
  } = usePlatformOperators();
  const createUser = useCreateOperatorAdmin();

  const [tenantId, setTenantId] = useState('');
  const [identityMode, setIdentityMode] = useState<'email' | 'username'>('email');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState('admin');
  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateOperatorAdminResult | null>(null);
  // The group code shown on the username-only success panel — captured from the
  // operator selected at submit time (the create result carries no slug).
  const [resultGroupCode, setResultGroupCode] = useState<string | undefined>(undefined);

  const selectedOperator = operators?.find((o) => o.tenantId === tenantId);

  function resetForm() {
    setTenantId('');
    setIdentityMode('email');
    setEmail('');
    setUsername('');
    setFullName('');
    setRole('admin');
    setFormError(null);
    setResult(null);
    setResultGroupCode(undefined);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    if (!tenantId) {
      setFormError('Select an operator for this user.');
      return;
    }
    if (identityMode === 'email' && !email.trim()) {
      setFormError('Enter an email address.');
      return;
    }
    if (identityMode === 'username' && !username.trim()) {
      setFormError('Enter a username.');
      return;
    }

    const identity: Pick<CreateOperatorAdminRequest, 'email' | 'username'> =
      identityMode === 'email'
        ? { email: email.trim() }
        : { username: username.trim() };

    createUser.mutate(
      {
        tenantId,
        body: { ...identity, role, ...splitName(fullName) },
      },
      {
        onSuccess: (data) => {
          setResult(data);
          setResultGroupCode(selectedOperator?.slug);
        },
        onError: (err) => setFormError(extractErrorMessage(err)),
      },
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        description="Add and manage team logins for BestAcco operators."
      />

      <div className="max-w-2xl">
        {result ? (
          <TempPasswordConfirmation
            result={result}
            groupCode={resultGroupCode}
            onDismiss={resetForm}
          />
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-brand-100 shrink-0" aria-hidden="true">
                <UserPlus size={16} className="text-brand-600" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Add user</h2>
                <p className="text-sm text-gray-500">
                  Create a team login for an operator, choose their role, and email
                  them a one-time password.
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <label
                  htmlFor="operator"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Operator (business/tenant) <span className="text-red-500">*</span>
                </label>
                {operatorsError ? (
                  <div className="flex items-center gap-2 text-sm text-red-700">
                    <AlertCircle size={16} aria-hidden="true" />
                    <span>Unable to load operators.</span>
                    <button
                      type="button"
                      onClick={() => void refetchOperators()}
                      className="font-medium text-brand-600 hover:text-brand-700 underline"
                    >
                      Retry
                    </button>
                  </div>
                ) : (
                  <select
                    id="operator"
                    value={tenantId}
                    onChange={(e) => setTenantId(e.target.value)}
                    disabled={operatorsLoading || createUser.isPending}
                    required
                    className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200 disabled:bg-gray-50 disabled:text-gray-400"
                  >
                    <option value="" disabled>
                      {operatorsLoading ? 'Loading operators…' : 'Select an operator…'}
                    </option>
                    {operators?.map((op) => (
                      <option key={op.tenantId} value={op.tenantId}>
                        {op.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <fieldset>
                <legend className="block text-sm font-medium text-gray-700 mb-1">
                  Identity <span className="text-red-500">*</span>
                </legend>
                <div className="flex flex-wrap gap-4" role="radiogroup" aria-label="Identity">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="identity-mode"
                      value="email"
                      checked={identityMode === 'email'}
                      onChange={() => setIdentityMode('email')}
                      disabled={createUser.isPending}
                      className="h-4 w-4 border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                    Email
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="identity-mode"
                      value="username"
                      checked={identityMode === 'username'}
                      onChange={() => setIdentityMode('username')}
                      disabled={createUser.isPending}
                      className="h-4 w-4 border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                    Username-only (no email)
                  </label>
                </div>
              </fieldset>

              {identityMode === 'email' ? (
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={createUser.isPending}
                    required
                    autoComplete="off"
                    placeholder="teammate@business.com"
                    className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200 disabled:bg-gray-50 disabled:text-gray-400"
                  />
                </div>
              ) : (
                <div>
                  <label
                    htmlFor="username"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Username <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={createUser.isPending}
                    required
                    autoComplete="off"
                    autoCapitalize="none"
                    placeholder="reception"
                    aria-describedby="username-help"
                    className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200 disabled:bg-gray-50 disabled:text-gray-400"
                  />
                  <p id="username-help" className="mt-1 text-xs text-gray-400">
                    Unique within{' '}
                    {selectedOperator ? selectedOperator.name : 'the group'}. Login
                    = group code + username. No invite email is sent.
                  </p>
                </div>
              )}

              <div>
                <label
                  htmlFor="user-role"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  id="user-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  disabled={createUser.isPending}
                  className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200 disabled:bg-gray-50 disabled:text-gray-400"
                >
                  <option value="admin">Admin — full access to the operator&apos;s properties</option>
                  <option value="revenue_manager">Revenue Manager — rates, availability &amp; reports</option>
                  <option value="operations">Operations — front desk &amp; housekeeping</option>
                  <option value="owner">Owner — read-only owner dashboard</option>
                </select>
                <p className="mt-1 text-xs text-gray-400">
                  Super-admin can&apos;t be assigned here.
                </p>
              </div>

              <div>
                <label
                  htmlFor="fullName"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Full name <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  disabled={createUser.isPending}
                  autoComplete="off"
                  placeholder="Jordan Smith"
                  className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200 disabled:bg-gray-50 disabled:text-gray-400"
                />
              </div>

              {formError && (
                <div
                  className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                  role="alert"
                >
                  <AlertCircle size={16} className="shrink-0 mt-0.5" aria-hidden="true" />
                  <span>{formError}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={createUser.isPending || operatorsLoading || operatorsError}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {createUser.isPending ? (
                  <>
                    <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                    Creating…
                  </>
                ) : (
                  <>
                    <UserPlus size={16} aria-hidden="true" />
                    Add user
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        <p className="mt-4 text-xs text-gray-400">
          New users are created as operator admins and must change their password on
          first login.
        </p>
      </div>

      <TeamMembersTable />
    </div>
  );
}
